import React from 'react';
import { Box } from '@mui/material';
import { useCompany } from '../../context/CompanyProvider';

/**
 * Shared print layout for branded FabWorks documents.
 * Renders the logo + company header + title, and then arbitrary children content.
 * Intended for use inside pages as a print-only block, paired with window.print().
 */
const PrintPage = ({ title, children, footer = null, showSpacer = true, rootClassName = '' }) => {
  const { company } = useCompany();
  const rootClass = ['print-container', 'print-quote', rootClassName].filter(Boolean).join(' ');
  return (
    <Box className={rootClass} sx={{ display: 'none' }}>
      <style>{`
        @media print {
          .no-print,
          .no-print-quotes,
          .no-print-customers,
          .no-print-hr,
          .no-print-shipments,
          .no-print-dashboard,
          .no-print-procurement-po,
          .no-print-vendor-invoices,
          .no-print-work-orders,
          .no-print-customer-detail,
          button,
          .MuiButton-root,
          .MuiIconButton-root,
          .MuiTabs-root,
          .MuiAppBar-root,
          .MuiDrawer-root,
          nav,
          .MuiModal-root,
          .MuiDialog-root,
          .MuiBackdrop-root,
          .MuiSnackbar-root {
            display: none !important;
          }
          .print-container {
            display: block !important;
          }
          /* Block flow so page breaks work; flex + min-height caused huge gaps on multi-page quotes */
          .print-quote-body {
            display: block !important;
            min-height: unset !important;
          }
          .print-quote-content {
            display: block !important;
          }
          .print-quote-spacer {
            display: none !important;
          }
          @page {
            size: letter;
            margin: 0.5in;
          }
          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
            -webkit-text-size-adjust: 100%;
          }
          .print-quote-top-row {
            position: relative !important;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) !important;
            align-items: start !important;
            width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
          }
          .print-quote-logo {
            position: static !important;
            left: auto !important;
            top: auto !important;
            max-width: 100% !important;
          }
          .print-quote-logo img {
            max-height: 110px !important;
            max-width: 220px !important;
            width: auto !important;
            height: auto !important;
          }
          .print-quote-company-header {
            position: static !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
            grid-column: 2 !important;
            justify-self: center !important;
          }
        }
      `}</style>

      <div className="print-quote-body">
        <div className="print-quote-content">
          <div className="print-quote-top-row">
            <div className="print-quote-logo">
              <img src={company.logos.print} alt={company.shortName} />
            </div>
            <div className="print-quote-company-header">
              <div className="print-quote-company-name">{company.legalName}</div>
              {company.print.addressLines.map((line) => (
                <div key={line} className="print-quote-company-address-line">
                  {line}
                </div>
              ))}
            </div>
          </div>

          <h1 className="print-quote-title">
            <em>{title}</em>
          </h1>

          {children}
        </div>

        {showSpacer && <div className="print-quote-spacer" aria-hidden="true" />}

        {footer}
      </div>
    </Box>
  );
};

export default PrintPage;

